import React, { useMemo, useState } from "react";

import {
  faArrowDown,
  faArrowsUpDown,
  faArrowUp,
  faCog,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper, HeaderContext } from "@tanstack/react-table";
import { AnimatePresence, m } from "framer-motion";
import { isEqual } from "lodash-es";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { CreateOperationModal } from "src/adminPanel/modulesV2/CreateOperationModal";
import { ModuleKind } from "src/adminPanel/modulesV2/constants";
import { EnableModuleModal } from "src/adminPanel/modulesV2/EnableModuleModal";
import { ModuleOperationsDetail } from "src/adminPanel/modulesV2/ModuleOperationsDetail";
import { SpecDiffModal } from "src/adminPanel/modulesV2/SpecDiffModal";
import { filterModulesBySearchTerm } from "src/adminPanel/modulesV2/utils";
import { Module } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Cell, TableComp } from "src/base-components/Table";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";

type Props = {
  modules: Module[];
  searchTerm: string;
  workspaceMap: Map<string, { name: string; orgId: string; slug: string }>;
  orgMap: Map<string, string>;
  moduleKind: ModuleKind;
  workspaces: WorkspaceDataplane[];
};

const columnHelper = createColumnHelper<Module>();

type SortableHeaderProps = {
  info: HeaderContext<Module, unknown>;
  children: React.ReactNode;
  showFilter?: boolean;
};

const SortableHeader: React.FC<SortableHeaderProps> = ({
  info,
  children,
  showFilter = false,
}) => {
  const column = info.column;
  const isSorted = column.getIsSorted();
  const canSort = column.getCanSort();
  const filterValue = (column.getFilterValue() as string) ?? "";
  let sortIcon = faArrowsUpDown;
  if (isSorted === "asc") {
    sortIcon = faArrowUp;
  } else if (isSorted === "desc") {
    sortIcon = faArrowDown;
  }

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <button
        className={twJoin(
          "flex items-center gap-2 whitespace-nowrap text-left font-inter-medium-12px text-gray-800",
          canSort && "cursor-pointer hover:text-gray-900",
        )}
        disabled={!canSort}
        type="button"
        onClick={() => canSort && column.toggleSorting()}
      >
        <span>{children}</span>
        {canSort && <Icon icon={sortIcon} size="2xs" />}
      </button>
      {showFilter && column.getCanFilter() && (
        <div className="min-w-[120px]">
          <Input
            placeholder="Filter..."
            type="text"
            value={filterValue}
            onChange={(e) => column.setFilterValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export const ModuleKindSection: React.FC<Props> = ({
  modules,
  searchTerm,
  workspaceMap,
  orgMap,
  moduleKind,
  workspaces,
}) => {
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);

  const existingWorkspaceIds = useMemo(
    () => new Set(modules.map((m) => m.workspaceId)),
    [modules],
  );
  const {
    isOpen: isDiffOpen,
    openModal: openDiffModal,
    closeModal: closeDiffModal,
    afterLeave: afterDiffLeave,
    data: diffModule,
  } = useModal<Module>();

  const {
    isOpen: isEnableOpen,
    openModal: openEnableModal,
    closeModal: closeEnableModal,
    afterLeave: afterEnableLeave,
  } = useModal();

  const {
    isOpen: isCreateOpOpen,
    openModal: openCreateOpModal,
    closeModal: closeCreateOpModal,
    afterLeave: afterCreateOpLeave,
    data: createOpModule,
  } = useModal<Module>();

  const filteredModules = filterModulesBySearchTerm(
    modules,
    searchTerm,
    workspaceMap,
    orgMap,
  );

  const toggleExpand = (moduleId: string) => {
    setExpandedModuleId((prev) => (prev === moduleId ? null : moduleId));
  };

  const expandedModule = filteredModules.find(
    (mod) => mod.id === expandedModuleId,
  );

  const getModuleContext = (mod: Module) => {
    const ws = workspaceMap.get(mod.workspaceId);
    return {
      workspaceName: ws?.name,
      orgName: ws ? orgMap.get(ws.orgId) : undefined,
    };
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => {
          const ws = workspaceMap.get(row.workspaceId);
          const orgName = ws ? orgMap.get(ws.orgId) : undefined;
          return ws ? `${orgName ?? ""} ${ws.orgId}` : "";
        },
        {
          id: "organization",
          header: (info) => (
            <SortableHeader info={info} showFilter>
              Organization
            </SortableHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            const orgName = ws ? orgMap.get(ws.orgId) : undefined;
            return (
              <Cell className="px-4" info={info}>
                {ws ? (
                  <span className="inline-flex items-center gap-1">
                    <Link
                      className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                      title={`Click to find organization ${orgName ?? ws.orgId} in the Workspaces tab`}
                      to={`/admin-panel/organizations?search=${ws.orgId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="font-inter-normal-13px">
                        {orgName ?? ws.orgId.slice(0, 8)}
                      </span>
                    </Link>
                    <span onClick={(e) => e.stopPropagation()}>
                      <CopyTextIcon
                        feedback="inline"
                        size="xs"
                        tooltip="Copy organization ID"
                        value={ws.orgId}
                      />
                    </span>
                  </span>
                ) : (
                  <span className="font-inter-normal-13px text-gray-500">
                    —
                  </span>
                )}
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: (row, columnId, filterValue: string) => {
            const mod = row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            if (!ws) return false;
            const orgName = orgMap.get(ws.orgId) ?? "";
            const lower = filterValue.toLowerCase();
            return (
              orgName.toLowerCase().includes(lower) ||
              ws.orgId.toLowerCase().includes(lower)
            );
          },
          meta: { displayName: "Organization" },
        },
      ),
      columnHelper.accessor(
        (row) => {
          const ws = workspaceMap.get(row.workspaceId);
          return ws?.name ?? row.workspaceId;
        },
        {
          id: "workspace",
          header: (info) => (
            <SortableHeader info={info} showFilter>
              Workspace
            </SortableHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            return (
              <Cell className="px-4" info={info}>
                <div className="flex items-center gap-1">
                  <div className="flex flex-col">
                    <Link
                      className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                      title="Click to find this workspace in the Workspaces tab"
                      to={`/admin-panel/organizations?search=${mod.workspaceId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="font-inter-normal-13px">
                        {ws?.name ?? mod.workspaceId.slice(0, 8)}
                      </span>
                    </Link>
                    {ws && (
                      <span className="font-inter-normal-12px text-gray-500">
                        {ws.slug}
                      </span>
                    )}
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <CopyTextIcon
                      feedback="inline"
                      size="xs"
                      tooltip="Copy workspace ID"
                      value={mod.workspaceId}
                    />
                  </span>
                </div>
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: (row, columnId, filterValue: string) => {
            const mod = row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            const lower = filterValue.toLowerCase();
            return (
              (ws?.name.toLowerCase().includes(lower) ?? false) ||
              (ws?.slug.toLowerCase().includes(lower) ?? false) ||
              mod.workspaceId.toLowerCase().includes(lower)
            );
          },
          meta: { displayName: "Workspace" },
        },
      ),
      columnHelper.accessor("id", {
        id: "moduleId",
        header: (info) => (
          <SortableHeader info={info} showFilter>
            Module ID
          </SortableHeader>
        ),
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <span className="inline-flex items-center gap-1">
              <span className="font-code-12 text-gray-900">
                {info.getValue().slice(0, 8)}
              </span>
              <span onClick={(e) => e.stopPropagation()}>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy module ID"
                  value={info.getValue()}
                />
              </span>
            </span>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "includesString",
        meta: { displayName: "Module ID" },
      }),
      columnHelper.accessor("desiredSpec.version", {
        id: "version",
        header: (info) => <SortableHeader info={info}>Version</SortableHeader>,
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <span className="inline-flex items-center gap-1">
              <code className="rounded bg-gray-200 px-2 py-1 font-code-12 text-gray-800">
                {info.getValue()}
              </code>
              <span onClick={(e) => e.stopPropagation()}>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy version"
                  value={info.getValue()}
                />
              </span>
            </span>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { displayName: "Version" },
      }),
      columnHelper.accessor("desiredSpec.is_active", {
        id: "active",
        header: (info) => <SortableHeader info={info}>Active</SortableHeader>,
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <Pill variant={info.getValue() ? "green" : "red"}>
              <Pill.Text fontType="text">
                {info.getValue() ? "Active" : "Inactive"}
              </Pill.Text>
            </Pill>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { displayName: "Active" },
      }),
      columnHelper.display({
        id: "specStatus",
        header: (info) => (
          <SortableHeader info={info}>Spec Status</SortableHeader>
        ),
        cell: (info) => {
          const mod = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              {mod.spec === null ? (
                <Pill variant="yellow">
                  <Pill.Text fontType="text">Pending</Pill.Text>
                </Pill>
              ) : isEqual(mod.spec, mod.desiredSpec) ? (
                <Pill variant="green">
                  <Pill.Text fontType="text">In Sync</Pill.Text>
                </Pill>
              ) : (
                <button
                  className="cursor-pointer"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDiffModal(mod);
                  }}
                >
                  <Pill variant="yellow">
                    <Pill.Text fontType="text">Drifted</Pill.Text>
                  </Pill>
                </button>
              )}
            </Cell>
          );
        },
        sortingFn: (rowA, rowB) => {
          const getOrder = (mod: Module) => {
            if (mod.spec === null) return 0;
            if (isEqual(mod.spec, mod.desiredSpec)) return 1;
            return 2;
          };
          return getOrder(rowA.original) - getOrder(rowB.original);
        },
        enableSorting: true,
        meta: { displayName: "Spec Status" },
      }),
      columnHelper.accessor((row) => row.activeOperations.length, {
        id: "activeOps",
        header: (info) => (
          <SortableHeader info={info}>Active Ops</SortableHeader>
        ),
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <span className="font-inter-normal-13px text-gray-900">
              {info.getValue()}
            </span>
          </Cell>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        meta: { displayName: "Active Ops" },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="w-10 px-4 py-3" />,
        cell: (info) => {
          const mod = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openCreateOpModal(mod);
                }}
              >
                <Icon icon={faCog} size="sm" />
              </Button>
            </Cell>
          );
        },
        meta: { displayName: "Actions" },
      }),
    ],
    [workspaceMap, orgMap, openDiffModal, openCreateOpModal],
  );

  const createOpContext = createOpModule
    ? getModuleContext(createOpModule)
    : { workspaceName: undefined, orgName: undefined };

  if (filteredModules.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-end">
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => openEnableModal()}
          >
            Enable Module
          </Button>
        </div>
        <div className="py-8 text-center font-inter-normal-13px text-gray-500">
          {searchTerm
            ? `No modules matching "${searchTerm}"`
            : "No modules of this kind"}
        </div>
        <EnableModuleModal
          afterLeave={afterEnableLeave}
          existingWorkspaceIds={existingWorkspaceIds}
          isOpen={isEnableOpen}
          moduleKind={moduleKind}
          orgMap={orgMap}
          workspaces={workspaces}
          onClose={closeEnableModal}
        />
        <CreateOperationModal
          afterLeave={afterCreateOpLeave}
          isOpen={isCreateOpOpen}
          module={createOpModule}
          orgName={createOpContext.orgName}
          workspaceName={createOpContext.workspaceName}
          onClose={closeCreateOpModal}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-inter-normal-13px text-gray-500">
          {filteredModules.length} module
          {filteredModules.length !== 1 ? "s" : ""}
        </span>
        <Button
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() => openEnableModal()}
        >
          Enable Module
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <TableComp
          clientSideFiltering
          columns={columns}
          data={filteredModules}
          disableVirtualization
          getRowId={(row) => row.id}
          rowPropsGetter={(row) => ({
            onClick: () => toggleExpand(row.original.id),
            classNameOverrides: twJoin(
              "cursor-pointer",
              expandedModuleId === row.original.id && "bg-gray-50",
            ),
          })}
          variant="default"
        />
        <AnimatePresence>
          {expandedModule && (
            <m.div
              key={expandedModule.id}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              style={{ overflow: "hidden" }}
              transition={{ duration: 0.2 }}
            >
              <div className="border-gray-200 border-t bg-gray-50">
                <ModuleOperationsDetail
                  module={expandedModule}
                  onCreateOperation={() => openCreateOpModal(expandedModule)}
                />
              </div>
            </m.div>
          )}
        </AnimatePresence>
        <SpecDiffModal
          afterLeave={afterDiffLeave}
          isOpen={isDiffOpen}
          module={diffModule}
          onClose={closeDiffModal}
        />
      </div>
      <EnableModuleModal
        afterLeave={afterEnableLeave}
        existingWorkspaceIds={existingWorkspaceIds}
        isOpen={isEnableOpen}
        moduleKind={moduleKind}
        orgMap={orgMap}
        workspaces={workspaces}
        onClose={closeEnableModal}
      />
      <CreateOperationModal
        afterLeave={afterCreateOpLeave}
        isOpen={isCreateOpOpen}
        module={createOpModule}
        orgName={createOpContext.orgName}
        workspaceName={createOpContext.workspaceName}
        onClose={closeCreateOpModal}
      />
    </div>
  );
};
